// Setup › DIGITAL EMPLOYEES › Standing orders & models (`/c/{slug}/setup/de-overrides`; DC-04
// routes `GET`/`PUT …/digital-employees/{id}/template-override`, DC-05 screen): the company's own
// override of a DE's template, exactly two fields (founder, Q43): the standing orders and the
// role-to-model map, every model resolved through the gateway (a model it does not serve, or the
// subscription path, is refused with the reason). Set by a company owner or admin or the DE's
// supervisor; it takes effect only with the DE's next deploy, never on the running DE. The
// override belongs to the template the DE runs (its latest deploy), or to the one already
// overridden. Three calls: the DEs, the company's deploys (which template each DE runs), the
// chosen DE's override.
import {
  Button,
  EmptyState,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SettingsGroup,
  SettingsRow,
  Textarea,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon, SlidersHorizontalIcon, Trash2Icon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { DeTemplateOverride } from "../../api/types";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { problemLines } from "../operator/templates/template-parts";
import { useOperatorName } from "../operator-name";
import { digitalEmployeesQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { RowAction } from "../settings/row-actions";
import { SetupPage } from "../settings/setup-page";
import { companyDeploysQuery } from "./deploy-approvals";

type Row = { key: number; role: string; model: string };

let nextRowKey = 0;
const row = (role: string, model: string): Row => ({ key: ++nextRowKey, role, model });

const overrideKey = (co: string, de: string) => ["template-override", co, de] as const;

export function TemplateOverrideScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const operator = useOperatorName();
  const des = useInfiniteQuery(digitalEmployeesQuery(api, company.id));
  const deploys = useQuery(companyDeploysQuery(api, company.id));
  const rows = (des.data?.pages.flatMap((p) => p.data) ?? []).filter((d) => d.status !== "retired");
  const [deId, setDeId] = useState("");
  const pickId = useId();
  const override = useQuery({
    queryKey: overrideKey(company.id, deId),
    queryFn: () =>
      api.client.digitalEmployees.templateOverride({ params: { co: company.id, id: deId } }),
    enabled: !!deId,
  });
  // The template the override is for: the one already overridden, else the DE's latest deploy's.
  const latest = (deploys.data?.data ?? []).find((d) => d.de_membership_id === deId) ?? null;
  // The template the override is for: the one already overridden, else the one the DE runs now
  // (the API's `current_template_id`), else its latest deploy's.
  const templateId =
    override.data?.override?.template_id ??
    override.data?.current_template_id ??
    latest?.template_id ??
    null;
  const templateName = latest && latest.template_id === templateId ? latest.template_name : null;

  return (
    <SetupPage title="Standing orders & models">
      {des.isPending || deploys.isPending ? (
        <Loading label="Loading digital employees" rows={2} />
      ) : des.isError ? (
        <LoadError what="Digital employees" error={des.error} />
      ) : deploys.isError ? (
        <LoadError what="Deploys" error={deploys.error} />
      ) : !rows.length ? (
        <EmptyState
          variant="card"
          icon={SlidersHorizontalIcon}
          title="No digital employees yet"
          description="Set up a digital employee first."
        />
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          <SettingsGroup>
            <SettingsRow
              label="Digital employee"
              hint="Changes apply with its next deploy."
              htmlFor={pickId}
            >
              <NativeSelect
                id={pickId}
                size="sm"
                className="max-md:w-full"
                value={deId}
                onChange={(e) => setDeId(e.target.value)}
              >
                <NativeSelectOption value="">Choose a digital employee</NativeSelectOption>
                {rows.map((d) => (
                  <NativeSelectOption key={d.membership_id} value={d.membership_id}>
                    {d.display_name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </SettingsRow>
          </SettingsGroup>
          {!deId ? null : override.isPending ? (
            <Loading label="Loading the override" rows={2} />
          ) : override.isError ? (
            <LoadError what="The override" error={override.error} />
          ) : !templateId ? (
            <Notice tone="neutral" data-slot="override-no-template">
              Nothing deployed to this digital employee yet: set its override after {operator}{" "}
              stages its first deploy.
            </Notice>
          ) : (
            <OverrideForm
              key={deId}
              api={api}
              company={company}
              deId={deId}
              templateId={templateId}
              templateName={templateName}
              current={override.data.override}
            />
          )}
        </div>
      )}
    </SetupPage>
  );
}

/**
 * GUIDED-FLOW: one DE's override (standing orders and models), the screen's form for a DE the
 * caller already knows (DE onboarding's "Models" step opens it in a modal). Same reads and rules.
 */
export function DeTemplateOverrideEditor({
  api,
  company,
  deId,
}: {
  api: Api;
  company: CompanyAccess;
  deId: string;
}) {
  const deploys = useQuery(companyDeploysQuery(api, company.id));
  const override = useQuery({
    queryKey: overrideKey(company.id, deId),
    queryFn: () =>
      api.client.digitalEmployees.templateOverride({ params: { co: company.id, id: deId } }),
  });
  const latest = (deploys.data?.data ?? []).find((d) => d.de_membership_id === deId) ?? null;
  // A stored override keeps its template (a template-less one stays null); otherwise the
  // template the DE runs now (`current_template_id`, never a newer failed or staged deploy's),
  // or none: a freshly hired DE edits its own row (`template_id: null`, which the PUT accepts).
  const stored = override.data?.override ?? null;
  const templateId = stored ? stored.template_id : (override.data?.current_template_id ?? null);
  const templateName = latest && latest.template_id === templateId ? latest.template_name : null;
  if (override.isPending || deploys.isPending)
    return <Loading label="Loading the override" rows={2} />;
  if (override.isError) return <LoadError what="The override" error={override.error} />;
  return (
    <OverrideForm
      api={api}
      company={company}
      deId={deId}
      templateId={templateId}
      templateName={templateName}
      current={override.data.override}
    />
  );
}

function rowsOf(map: DeTemplateOverride["role_model_map"]): Row[] {
  return Object.entries(map ?? {})
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([role, e]) => row(role, e.model));
}

function OverrideForm({
  api,
  company,
  deId,
  templateId,
  templateName,
  current,
}: {
  api: Api;
  company: CompanyAccess;
  deId: string;
  /** null: the DE's own, template-less override (nothing deployed yet). */
  templateId: string | null;
  templateName: string | null;
  current: DeTemplateOverride | null;
}) {
  const qc = useQueryClient();
  const [orders, setOrders] = useState(current?.standing_orders_text ?? "");
  const [models, setModels] = useState<Row[]>(rowsOf(current?.role_model_map ?? null));
  const [saved, setSaved] = useState(false);
  const ids = { orders: useId(), models: useId() };
  const save = useMutation({
    mutationFn: () => {
      const map = Object.fromEntries(
        models
          .filter((r) => r.role.trim() && r.model.trim())
          .map((r) => [r.role.trim(), { model: r.model.trim(), paths: ["gateway" as const] }]),
      );
      return api.client.digitalEmployees.putTemplateOverride({
        params: { co: company.id, id: deId },
        body: {
          template_id: templateId,
          standing_orders_text: orders.trim() ? orders : null,
          role_model_map: Object.keys(map).length ? map : null,
        },
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: overrideKey(company.id, deId) });
      setSaved(true);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setSaved(false);
    save.mutate();
  };
  const setRow = (key: number, patch: Partial<Row>) =>
    setModels((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  return (
    <form
      id="template-override"
      onSubmit={submit}
      className="flex flex-col gap-4"
      data-slot="template-override-form"
    >
      <SettingsGroup
        title={
          templateName
            ? `Override of ${templateName}`
            : templateId
              ? "Override of the DE's template"
              : "The DE's own settings"
        }
        actions={
          <span className="text-xs text-muted-foreground">
            {current
              ? `v${current.version} · ${formatDateTime(current.updated_at)}`
              : "Template defaults"}
          </span>
        }
      >
        <SettingsRow
          label="Standing orders"
          hint="Given verbatim to every worker the DE starts. Empty: the template's own."
          htmlFor={ids.orders}
          wide
        >
          <Textarea
            id={ids.orders}
            rows={5}
            maxLength={20000}
            value={orders}
            onChange={(e) => setOrders(e.target.value)}
          />
        </SettingsRow>
        <SettingsRow
          label="Role-to-model map"
          hint="Each role's model, through the company's gateway. Empty: the template's own."
          wide
        >
          <fieldset className="flex w-full flex-col gap-1.5" aria-label="Role-to-model map">
            {models.map((r) => (
              <div key={r.key} className="flex items-center gap-2" data-slot="override-model-row">
                <Input
                  id={`${ids.models}-role-${r.key}`}
                  aria-label="Role"
                  placeholder="Role"
                  className="h-8 w-36 max-md:w-28"
                  value={r.role}
                  onChange={(e) => setRow(r.key, { role: e.target.value })}
                />
                <Input
                  id={`${ids.models}-model-${r.key}`}
                  aria-label="Model"
                  placeholder="Model"
                  className="h-8 min-w-0 flex-1"
                  value={r.model}
                  onChange={(e) => setRow(r.key, { model: e.target.value })}
                />
                <RowAction
                  type="button"
                  icon={Trash2Icon}
                  label={`Remove the ${r.role || "new"} role`}
                  tip="Remove"
                  className="hover:text-danger-text"
                  onClick={() => setModels((rs) => rs.filter((x) => x.key !== r.key))}
                />
              </div>
            ))}
            <div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setModels((rs) => [...rs, row(rs.length ? "" : "lead", "")])}
              >
                <PlusIcon data-icon="inline-start" aria-hidden="true" />
                Add a role
              </Button>
            </div>
          </fieldset>
        </SettingsRow>
      </SettingsGroup>
      {save.isError ? (
        <Notice tone="danger" role="alert" title="The override was not saved">
          {problemLines(save.error, "The override could not be saved.").join(" · ")}
        </Notice>
      ) : saved ? (
        <Notice tone="success" role="status" data-slot="override-saved">
          Saved. It takes effect with the DE's next deploy.
        </Notice>
      ) : null}
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save override"}
        </Button>
      </div>
    </form>
  );
}
